import { ServiceError } from "../../../../../service/Shared";
import { getErrorMessageFromCode } from "../../../../../service/ValidationErrorFormatter";

// The api names the column it rejected by its request property; these are what a person calls it.
export const PROJECT_FIELD_LABELS: Record<string, string> = {
  StandardUuid: "Standard",
  ProjectTypeUuid: "Project type",
  DisplayName: "Project name",
};

// createGroup validates the landholding itself alongside its member projects, and its errors carry
// no "Projects[i]" prefix to tell the two apart - these are the landholding's own request fields,
// named as the landholding form's own labels rather than the api's request property names.
export const LANDHOLDING_FIELD_LABELS: Record<string, string> = {
  Name: "Landholding name",
  ExternalParty: "Landholding reference",
  ExternalReference: "Landholding reference ID",
};

// A bulk row's own field is pathed "Projects[i].Property" (or "projects[i].Property") - only the
// trailing segment is a field name PROJECT_FIELD_LABELS/LANDHOLDING_FIELD_LABELS, or a message's
// {property} placeholder, know how to put in front of the person who filled it in.
export const trailingPropertyName = (property: string): string => property.split(".").pop() ?? property;

// Stamped (as parameters.scope) onto an uploaded landholding's own errors by callProjectDetails,
// which gives them a row to be shown against; the views read it to tell such an error from the
// row's own, since the same row key carries both.
export const LANDHOLDING_ERROR_SCOPE = "landholding";

// The api spells a landholding property two ways: "ExternalReference" from the request's own
// annotations, "externalReference" from the rule that finds the reference already taken - so the
// label is looked up without regard to case.
export const landholdingFieldLabel = (property: string | undefined): string | undefined => {
  if (!property) return undefined;
  const wanted = trailingPropertyName(property).toLowerCase();
  const match = Object.keys(LANDHOLDING_FIELD_LABELS).find((key) => key.toLowerCase() === wanted);
  return match ? LANDHOLDING_FIELD_LABELS[match] : undefined;
};

// The message for one project or landholding error, with no project prefix - callers decide how to
// label it.
export const resolveCreateProjectErrorMessage = (error: ServiceError): string => {
  const property = error.property ? trailingPropertyName(error.property) : undefined;

  if (error.code === "VALIDATION_ATTRIBUTE_REQUIRED" && property) {
    const label = PROJECT_FIELD_LABELS[property] ?? landholdingFieldLabel(property);
    if (label) return `${label} is a required field`;
  }

  // property is filled in here rather than left to the api: a bulk error's own parameters (say
  // minLength/maxLength) never carry it, and without it "{property} must be..." goes out unresolved.
  return getErrorMessageFromCode(error.code, { property, ...error.parameters });
};

// createGroup's own errors carry none of the "Projects[i]" prefix a member row's do, so a landholding
// field is picked out by its property being one the landholding request itself owns.
export const getLandholdingErrors = (errors: ServiceError[] | undefined): ServiceError[] =>
  (errors ?? [])
    .filter((error) => !!landholdingFieldLabel(error.property))
    .map((error) => ({ ...error, message: resolveCreateProjectErrorMessage(error) }));
